import { FetchQueryOptions, QueryClient } from '@tanstack/react-query';

import type {
  IPublicDesktopV2OffersGetOffersRequest,
  IPublicDesktopV2OffersGetOffersResponse,
} from 'shared/repositories/commercial-search-offers/desktop/v2/offers/get-offers';
import type { IApplicationContext } from 'shared/types/ApplicationContext';
import { JsonQuery } from 'shared/types/JsonQuery';
import { extractUndefined } from 'shared/utils/extractUndefined';
import { getCancellationTokenFromSignal } from 'shared/utils/getCancellationTokenFromSignal';

import { QueryError, QueryKey, getQueryKey } from './internal/config';
import { fetchGetOffersService } from './internal/fetchService';

export class FetchGetOffersLoadService {
  private readonly queryClient: QueryClient;

  public constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  public prefetchQuery<TData = IPublicDesktopV2OffersGetOffersResponse>(
    applicationContext: IApplicationContext,
    jsonQuery: JsonQuery,
    options?: FetchQueryOptions<IPublicDesktopV2OffersGetOffersResponse, QueryError, TData, QueryKey>,
  ): Promise<void> {
    const parameters: IPublicDesktopV2OffersGetOffersRequest = { jsonQuery };

    return this.queryClient.prefetchQuery<IPublicDesktopV2OffersGetOffersResponse, QueryError, TData, QueryKey>({
      queryFn: ({ signal }) =>
        fetchGetOffersService({
          applicationContext,
          cancellationToken: getCancellationTokenFromSignal(signal),
          parameters,
        }),
      queryKey: getQueryKey(),
      ...options,
    });
  }

  public fetchQuery<TData = IPublicDesktopV2OffersGetOffersResponse>(
    applicationContext: IApplicationContext,
    jsonQuery: JsonQuery,
    options?: FetchQueryOptions<IPublicDesktopV2OffersGetOffersResponse, QueryError, TData, QueryKey>,
  ): Promise<TData> {
    const parameters: IPublicDesktopV2OffersGetOffersRequest = { jsonQuery };

    return this.queryClient.fetchQuery<IPublicDesktopV2OffersGetOffersResponse, QueryError, TData, QueryKey>({
      queryFn: ({ signal }) =>
        fetchGetOffersService({
          applicationContext,
          cancellationToken: getCancellationTokenFromSignal(signal),
          parameters,
        }),
      queryKey: getQueryKey(),
      ...options,
    });
  }

  public getQueryData(): IPublicDesktopV2OffersGetOffersResponse | null {
    return extractUndefined(this.queryClient.getQueryData<IPublicDesktopV2OffersGetOffersResponse>(getQueryKey()));
  }

  public setQueryData(
    updater: IPublicDesktopV2OffersGetOffersResponse,
  ): IPublicDesktopV2OffersGetOffersResponse | null {
    return extractUndefined(
      this.queryClient.setQueryData<IPublicDesktopV2OffersGetOffersResponse>(getQueryKey(), updater),
    );
  }

  public updateFavoriteByOfferId(id: number, newFavoriteValue: boolean): void {
    const prev = this.getQueryData();

    if (prev) {
      for (const offer of prev.offers) {
        if (offer.id === id) {
          offer.data.extendedProperties.isFavorite = newFavoriteValue;

          break;
        }
      }
    }
  }
}
